import React, { useState } from 'react';
import { Box } from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkIcon from '@mui/icons-material/Link';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i;
const VIDEO_EXT = /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i;
const PDF_EXT = /\.pdf(\?|$)/i;

const IMAGE_TYPES = ['image'];
const VIDEO_TYPES = ['video', 'virtual_tour', 'drone_footage'];
const PDF_TYPES = ['brochure', 'floor_plan', 'site_plan', 'master_plan', 'price_list', 'payment_plan', 'cost_sheet', 'rera_document', 'sale_deed', 'title_deed', 'encumbrance_certificate', 'occupancy_certificate', 'completion_certificate', 'noc', 'inventory_sheet', 'booking_form', 'agreement_for_sale', 'allotment_letter'];

function isImageUrl(url) {
  return url && typeof url === 'string' && IMAGE_EXT.test(url);
}

function isVideoUrl(url) {
  return url && typeof url === 'string' && VIDEO_EXT.test(url);
}

function isPdfUrl(url) {
  return url && typeof url === 'string' && PDF_EXT.test(url);
}

/**
 * Renders a thumbnail for media: image (img), PDF (embed/icon), video (video + play icon), or document icon.
 * Uses asset_type and optional url extension to decide. Falls back to icon on error or unsupported type.
 */
function MediaThumbnail({ url, assetType, size = 80, borderRadius = 8, fallbackColor = '#00c46d', sx = {} }) {
  const [error, setError] = useState(false);

  // Prefer URL extension so e.g. floor_plan + .png URL shows image thumbnail
  const hasUrl = url && typeof url === 'string' && url.startsWith('http');
  const isImage = isImageUrl(url) || IMAGE_TYPES.includes(assetType);
  const isVideo = !isImage && (isVideoUrl(url) || VIDEO_TYPES.includes(assetType));
  const isPdf = !isImage && !isVideo && (isPdfUrl(url) || PDF_TYPES.includes(assetType));

  const boxSx = {
    width: size,
    height: size,
    borderRadius,
    flexShrink: 0,
    overflow: 'hidden',
    background: fallbackColor + '18',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    ...sx,
  };

  const fallbackIcon = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      {hasUrl ? (
        <LinkIcon sx={{ fontSize: size * 0.4, color: fallbackColor }} />
      ) : (
        <InsertDriveFileOutlinedIcon sx={{ fontSize: size * 0.45, color: fallbackColor }} />
      )}
    </Box>
  );

  if (error || !hasUrl) {
    return (
      <Box sx={boxSx}>
        {isPdf && !error ? (
          <PictureAsPdfIcon sx={{ fontSize: size * 0.5, color: fallbackColor }} />
        ) : (
          fallbackIcon
        )}
      </Box>
    );
  }

  if (isImage) {
    return (
      <Box sx={boxSx} component="span">
        <img
          src={url}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
          onError={() => setError(true)}
        />
      </Box>
    );
  }

  if (isVideo) {
    return (
      <Box sx={boxSx} component="span">
        <video
          src={url}
          preload="metadata"
          muted
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onError={() => setError(true)}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,.25)',
            pointerEvents: 'none',
          }}
        >
          <PlayCircleOutlineIcon sx={{ fontSize: size * 0.5, color: '#fff' }} />
        </Box>
      </Box>
    );
  }

  // PDF: show icon only. Avoid <object>/<embed> so the browser doesn't request the PDF
  // on tab load and trigger an unwanted download (e.g. when server sends Content-Disposition: attachment).
  if (isPdf) {
    return (
      <Box sx={boxSx} component="span">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <PictureAsPdfIcon sx={{ fontSize: size * 0.5, color: fallbackColor }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={boxSx}>
      <InsertDriveFileOutlinedIcon sx={{ fontSize: size * 0.45, color: fallbackColor }} />
    </Box>
  );
}

export default MediaThumbnail;
